// Pre-rendered ArcGIS tile-cache rendering, for the Scottish soil and land
// capability layers (BBB-457).
//
// WHY THIS EXISTS, separately from arcgisImageMapType: both adapters talk to
// ArcGIS, but to two different kinds of service. arcgisImageMapType builds an
// `/export?bbox=...` request, which only a dynamic map service answers. The
// Hutton replacements published on ArcGIS Online are `TilesOnly` fused caches
// (verified 18/08/2026: `/export` on them returns 400 or 404), so they are only
// reachable through `/tile/{level}/{row}/{col}`.
//
// WHY IT DOES NOT JUST USE ImageMapType. A fused cache only contains the levels
// it was built to, and these two were built shallower than they advertise.
// Measured with real requests rather than read off the LOD list:
//
//   soil (Hutton_Soils_250K_SSKIB_OSGB_AGOL_FL)   built to z14
//   land capability (Land_Capability_for_Agriculture) built to z13
//
// A zone opens at FIELD_ZOOM 18 (see lib/zoneBounds.js), so on the plain path
// both layers would ask for tiles that do not exist, get a 404, and draw nothing
// at exactly the zoom the field is looked at. The old druid services never hit
// this because a dynamic service renders any bbox at any scale.
//
// So past `maxCachedZoom` this draws the deepest tile that does exist, scaled up
// and cropped to the sub-region the requested tile covers. Nothing real is lost:
// at field zoom a whole field sits inside one soil polygon and one capability
// polygon, so the result is a solid block of the correct class colour, which is
// what a 1:50,000 survey actually knows. `image-rendering: pixelated` keeps the
// class boundaries as hard steps rather than smearing two classes into a blend
// that means nothing. Davin chose this over capping the layers, 18/08/2026.

import { createImgTileMapType } from './imgTileMapType';

const TILE_PX = 256;

function normaliseCoord(coord, zoom) {
  const n = 2 ** zoom;
  if (coord.y < 0 || coord.y >= n) return null;
  return {
    x: ((coord.x % n) + n) % n,
    y: coord.y,
  };
}

/**
 * Build the tile URL for one tile of a fused ArcGIS cache.
 *
 * @param {object} source catalogue source (needs serviceUrl)
 * @param {{x: number, y: number}} coord Google tile coordinate
 * @param {number} zoom Google Maps zoom level
 * @returns {string|null} tile URL, or null for "no tile here"
 */
export function buildArcgisCachedTileUrl(source, coord, zoom) {
  // Same guard as the other two adapters: a null coord or non-finite zoom
  // arrives during rapid mobile zoom, and would otherwise emit a URL with NaN
  // in the path. null is Google's documented "no tile here" and won't wedge
  // the layer.
  if (!coord || typeof zoom !== 'number' || !Number.isFinite(zoom)) return null;

  const wrapped = normaliseCoord(coord, zoom);
  if (!wrapped) return null;

  // ArcGIS orders the path level/row/col, which is zoom/y/x. Getting the last
  // two the wrong way round returns real tiles from the wrong place rather
  // than an error, so it fails silently: hence the explicit naming.
  const level = zoom;
  const row = wrapped.y;
  const col = wrapped.x;

  return `${source.serviceUrl}/tile/${level}/${row}/${col}`;
}

/**
 * Work out which cached tile to fetch for a requested tile, and how to place it.
 *
 * At or below `maxCachedZoom` this is the tile itself at natural size. Above it,
 * the answer is an ancestor tile blown up by 2 per level and shifted so that the
 * requested tile's share of it lands in the frame.
 *
 * Pure, and exported, because the arithmetic is the part that can be wrong in a
 * way nobody notices: an offset error shows the neighbouring field's soil rather
 * than an error.
 *
 * @param {object} source catalogue source (serviceUrl, optional maxCachedZoom)
 * @param {{x: number, y: number}} coord Google tile coordinate
 * @param {number} zoom Google Maps zoom level
 * @returns {{url: string, sizePx: number, offsetXPx: number, offsetYPx: number}|null}
 */
export function resolveCachedTile(source, coord, zoom) {
  if (!coord || typeof zoom !== 'number' || !Number.isFinite(zoom)) return null;

  const wrapped = normaliseCoord(coord, zoom);
  if (!wrapped) return null;

  const cap = source.maxCachedZoom;
  // No cap declared, or already within the cache: the plain one-to-one case.
  if (typeof cap !== 'number' || zoom <= cap) {
    const url = buildArcgisCachedTileUrl(source, coord, zoom);
    return url && { url, sizePx: TILE_PX, offsetXPx: 0, offsetYPx: 0 };
  }

  const scale = 2 ** (zoom - cap);
  const ancestor = { x: Math.floor(wrapped.x / scale), y: Math.floor(wrapped.y / scale) };
  const url = buildArcgisCachedTileUrl(source, ancestor, cap);
  if (!url) return null;

  // Which of the scale x scale cells of the blown-up ancestor this tile is.
  const cellX = wrapped.x % scale;
  const cellY = wrapped.y % scale;

  return {
    url,
    sizePx: TILE_PX * scale,
    // Negating zero gives -0, which would reach the DOM as `left: -0px`. Harmless
    // to render, but it makes the value awkward to assert on and odd to read in
    // devtools, so the top-left cell is spelled out as a plain zero.
    offsetXPx: cellX === 0 ? 0 : -cellX * TILE_PX,
    offsetYPx: cellY === 0 ? 0 : -cellY * TILE_PX,
  };
}

export function createArcgisTileMapType(source, opacity, onOutcome) {
  // The frame, the img, the opacity and the release are all shared with the WMS
  // and dynamic-export adapters now: see lib/adapters/imgTileMapType.js. What is
  // specific to a fused cache is the placement arithmetic above, which is what
  // `resolve` hands over, and `pixelated`, which keeps class boundaries as hard
  // steps rather than smearing two soil classes into a blend that means nothing.
  return createImgTileMapType({
    id: source.id,
    opacity,
    onOutcome,
    pixelated: true,
    resolve: (coord, zoom) => resolveCachedTile(source, coord, zoom),
  });
}
